import React, { useMemo } from "react";
import { UI_CAPTION, SkeletonLoader } from "oolib";
import { Files, FileText } from "@phosphor-icons/react";
import moment from "moment";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { useGetDataQuery } from "@/utils/react-query-hooks/getData";
import { StatCardGroup, StatCard } from "../Stats";
import { StyledStatCard } from "../Stats/styled";
import {
  useGetAnnotationStatusOverview,
  getAnnotationCoverage,
} from "../../hooks/useGetAnnotationStatusOverview";

/**
 * Tooltip body for the "% Analysed" stat — explains the plain coverage ratio
 * and shows the live annotated/published counts behind it.
 */
const AnalysisScoreTooltip = ({ coverage }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
    <UI_CAPTION style={{ fontWeight: 600 }}>How “Analysed” is calculated</UI_CAPTION>
    <UI_CAPTION>
      An <b>annotated</b> document is any published document with at least one annotation.
      <b> Published</b> documents are all documents published on the platform.
    </UI_CAPTION>
    <UI_CAPTION>Analysed =annotated documents ÷ published documents × 100.</UI_CAPTION>
    {coverage && (
      <UI_CAPTION style={{ marginTop: 2 }}>
        {coverage.totalAnnotated.toLocaleString()} annotated ÷{" "}
        {coverage.totalPublished.toLocaleString()} published = {coverage.percent}%
      </UI_CAPTION>
    )}
  </div>
);

/**
 * Tooltip body for the "Time Saved" stat — what the figure is measured
 * against (tagging the same documents by hand) and the per-activity
 * (count × minutes) breakdown behind it. Annotation work only.
 */
const TimeSavedTooltip = ({ breakdown }) => {
  if (!Array.isArray(breakdown)) return null;
  const items = breakdown.filter((b) => b.count > 0);
  if (!items.length) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <UI_CAPTION style={{ fontWeight: 600 }}>Time saved vs. manual tagging</UI_CAPTION>
      <UI_CAPTION>
        An estimate of the time it would take to read and tag the same documents by hand, per document:
      </UI_CAPTION>
      {items.map((b) => (
        <UI_CAPTION key={b.id}>
          {b.label}: {b.count.toLocaleString()} x {b.minutesPer} min = {Math.round((b.totalMinutes / 60) * 10) / 10} hrs
        </UI_CAPTION>
      ))}
    </div>
  );
};

/**
 * Shared dashboard stats bar — the top-line metrics row used by BOTH the
 * General and Lenses tabs. Single source of truth so the two stay in sync.
 * "% Analysed" and "Time Saved" each carry an info-icon tooltip explaining
 * how the figure is calculated.
 */
// The bar shows whole numbers. Anything real but under one reads "<1",
// never "0" — the sources keep a decimal for exactly that reason (0.4%
// analysed, ten minutes saved), and the tooltips still show it.
const wholeOrUnderOne = (n) => (n > 0 && n < 1 ? "<1" : Math.round(n || 0).toLocaleString());

// Documents published inside the period, as the platform's own date-range
// filter. That filter reads its edges as DAYS and extends the end by one, so
// the period's exclusive end (the next period's first instant) is handed
// over a day early — the count then stops exactly at the boundary.
const publishedInFilter = (period) =>
  period?.from && period?.to
    ? {
        filterId: "publishedIn",
        target: { filterType: "dateRangeType", valuePath: "kp_date_published" },
        values: [period.from, moment(period.to).subtract(1, "day").toISOString()],
      }
    : null;

// How a period reads in "+n in <period>": a month as "Jul '26"; any other
// period (a year, a week, all time) by its own name.
const periodLabel = (period) =>
  /^month_|_month$/.test(period?.value || "") ? moment(period.from).format("MMM 'YY") : period?.display;

// One card's shape while its number loads: the title line (icon + label)
// and the value line under it, where those will land.
export const StatCardSkeleton = () => (
  <StyledStatCard>
    <SkeletonLoader style={{ height: 14, width: 120, borderRadius: 4 }} />
    <SkeletonLoader style={{ height: 26, width: 72, borderRadius: 4 }} />
  </StyledStatCard>
);

// The bar's shape while the metrics load. `cards` = how many the bar will
// hold, so a page that appends cards of its own keeps the same width.
export function DashboardStatsBarSkeleton({ cards = 5 }) {
  return (
    <StatCardGroup>
      {Array.from({ length: cards }, (_, i) => (
        <StatCardSkeleton key={i} />
      ))}
    </StatCardGroup>
  );
}

// `children`: extra cards a page appends to the same group, after the
// platform-wide five (the New tab adds the Priorities readings).
// `period`: a page that reads its data by period (the New tab: the latest
// month) passes it, and the Documents card says how many documents LANDED
// on the platform in it — by publish date, the ordinary document count
// (getData) over every dataset. The bar owns that request: it feeds only
// this card.
export function DashboardStatsBar({ metrics, children, period = null }) {
  // "% Analysed" reuses the same cached query as the Annotate Now widget
  // (annotated docs / published docs), not the legacy weighted analysisScore.
  const { data: overview } = useGetAnnotationStatusOverview();
  const coverage = getAnnotationCoverage(overview);
  // The reports listing lives where the tenant's base configs say (same
  // link the Featured reports widget uses).
  const { SELF_MANAGED_BASE_CONFIGS } = useGetQueryData("platformConfigs") || {};
  const reportsListingLink = SELF_MANAGED_BASE_CONFIGS?.reports?.linkToReportsListing || "/reports";
  const allTpls = useGetQueryData("allTpls");
  const datasetContentTypes = useMemo(
    () => (allTpls || []).filter((t) => t.general?.segment === "publishing").map((t) => t.kp_content_type),
    [allTpls]
  );
  const periodFilters = useMemo(() => [publishedInFilter(period)].filter(Boolean), [period]);
  const { data: docsInPeriod } = useGetDataQuery({
    contentTypes: datasetContentTypes,
    activeFilters: periodFilters,
    findQuery: { kp_published_status: "published" },
    useCountDAL: true,
    queryOptions: { enabled: periodFilters.length > 0 && datasetContentTypes.length > 0, staleTime: 5 * 60 * 1000 },
  });
  // Count mode answers one { count, contentType } per dataset.
  const docsInPeriodSubline =
    period && docsInPeriod
      ? `+${(docsInPeriod.data || []).reduce((sum, d) => sum + (d.count || 0), 0).toLocaleString()} in ${periodLabel(period)}`
      : undefined;

  if (!metrics) return null;
  return (
    <StatCardGroup>
      <StatCard icon="Database" title="Datasets" value={String(metrics.totalDatasets)} link={{ label: "View Datasets", to: "/datasets" }} />
      <StatCard iconComp={Files} title="Documents" value={metrics.totalDocs.toLocaleString()} subline={docsInPeriodSubline} />
      <StatCard
        icon="ChartDonut"
        title="Analysed"
        value={`${wholeOrUnderOne(coverage?.percent)}%`}
        subline={
          coverage
            ? `${coverage.totalAnnotated.toLocaleString()} of ${coverage.totalPublished.toLocaleString()} documents`
            : undefined
        }
        progress={coverage?.percent ?? 0}
        // infoTooltip={<AnalysisScoreTooltip coverage={coverage} />}
      />
      <StatCard
        // The same icon the platform sidebar's Reports entry carries.
        iconComp={FileText}
        title="Reports"
        value={String(metrics.reportsCreated)}
        // No reports yet: the way in is to make one — the listing opens
        // with its create modal up (?create=1). Otherwise, the listing.
        link={
          metrics.reportsCreated > 0
            ? { label: "View Reports", to: reportsListingLink }
            : { label: "Create a Report", to: `${reportsListingLink}${reportsListingLink.includes("?") ? "&" : "?"}create=1` }
        }
      />
      <StatCard
        icon="Clock"
        title="Time Saved"
        // The full whole-hour figure, not the source's "2.6K hrs" short form.
        value={`${wholeOrUnderOne(metrics.timeSaved?.hours)} hrs`}
        subline="vs. manual tagging"
        infoTooltip={<TimeSavedTooltip breakdown={metrics.timeSaved?.breakdown} />}
        infoTooltipOn="subline"
      />
      {children}
    </StatCardGroup>
  );
}
